<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { AvatarFallback, AvatarImage, AvatarRoot } from 'reka-ui';
import { ChevronDown, Menu, X } from '@lucide/vue';
import { gotoLogin, clearToken } from '@/utils/auth';
import { useUserStore } from '@/stores';
import logoSrc from '../../../assets/openatom.png';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const mobileMenuOpen = ref(false);

const isLoggedIn = computed(() => Boolean(userStore.user.id));
const displayName = computed(() => userStore.user.nickname || userStore.user.username || '用户');
const userInitial = computed(() => displayName.value.charAt(0).toUpperCase());

// 落地页锚点导航（仅在首页生效，其它路由点击会先回首页再滚动）
const sectionLinks = [
  { label: '首页', hash: 'hero' },
  { label: '考试科目', hash: 'exams' },
  { label: '报名流程', hash: 'process' },
  { label: '考点考场', hash: 'locations' },
  { label: '常见问题', hash: 'faq' },
];

const activeSection = ref('');

function doScroll(hash: string) {
  document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
}

function scrollToSection(hash: string) {
  closeMobileMenu();
  if (route.path !== '/') {
    router.push('/');
    nextTick(() => setTimeout(() => doScroll(hash), 60));
  }
  else {
    doScroll(hash);
  }
}

function isSectionActive(hash: string) {
  return route.path === '/' && activeSection.value === hash;
}

function onScroll() {
  if (route.path !== '/') {
    return;
  }
  const ids = sectionLinks.map(l => l.hash);
  let cur = ids[0];
  for (const id of ids) {
    const el = document.getElementById(id);
    if (el && el.offsetTop <= window.scrollY + 120) {
      cur = id;
    }
  }
  activeSection.value = cur;
}

function goUserCenter() {
  closeMobileMenu();
  if (isLoggedIn.value) {
    router.push('/user');
  }
  else {
    gotoLogin();
  }
}

function closeMobileMenu() {
  mobileMenuOpen.value = false;
}

function toggleMenu() {
  mobileMenuOpen.value = !mobileMenuOpen.value;
}

const handleLogin = () => {
  gotoLogin();
};

function handleLogout() {
  clearToken();
  userStore.resetInfo();

  // 退出后回首页，不自动跳登录
  router.push('/');
}

onMounted(() => {
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
});

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll);
});

watch(() => route.path, () => {
  nextTick(onScroll);
});
</script>

<template>
  <header
    class="sticky top-0 z-100 border-b border-border-subtle bg-[oklch(100%_0_0/0.85)] backdrop-blur-[16px]"
    :class="mobileMenuOpen ? 'max-lg:bg-surface-card max-lg:backdrop-blur-none' : ''"
  >
    <div class="container-main flex items-center justify-between h-16">
      <!-- 品牌 -->
      <div class="flex items-center gap-4">
        <RouterLink to="/" class="inline-flex items-center no-underline">
          <img
            :src="logoSrc"
            alt="培训认证报名系统"
            class="h-7 md:h-8 w-auto align-middle"
          >
        </RouterLink>
      </div>

      <!-- 桌面端导航 -->
      <nav aria-label="主导航" class="hidden lg:flex items-center gap-2">
        <template v-for="link in sectionLinks" :key="link.hash">
          <a
            role="button"
            tabindex="0"
            class="px-3 lg:px-4 py-2 rounded-[10px] text-sm font-medium transition-all duration-150 ease-out cursor-pointer border-none bg-transparent no-underline"
            :class="isSectionActive(link.hash)
              ? 'text-(--color-primary) bg-(--color-primary-light) font-semibold'
              : 'text-text-secondary hover:text-(--color-primary) hover:bg-(--color-primary-subtle)'"
            @click="scrollToSection(link.hash)"
          >
            {{ link.label }}
          </a>
        </template>

        <!-- 用户中心 -->
        <a
          v-if="isLoggedIn"
          role="button"
          tabindex="0"
          class="px-3 lg:px-4 py-2 rounded-[10px] text-sm font-medium transition-all duration-150 ease-out cursor-pointer border-none bg-transparent no-underline text-text-secondary hover:text-(--color-primary) hover:bg-(--color-primary-subtle)"
          @click="goUserCenter"
        >
          用户中心
        </a>

        <!-- 未登录 -->
        <button
          v-if="!isLoggedIn"
          type="button"
          class="px-3 lg:px-4 py-2 rounded-[10px] text-xs font-semibold transition-all duration-250 ease-out no-underline whitespace-nowrap min-h-9 bg-(--color-primary) text-white shadow-[var(--shadow-sm)] hover:bg-(--color-primary-hover) hover:shadow-[var(--shadow-md)] hover:-translate-y-px cursor-pointer border-none"
          @click="handleLogin"
        >
          登录
        </button>

        <!-- 已登录 -->
        <DropdownMenu v-else :modal="false">
          <DropdownMenuTrigger as-child>
            <button
              type="button"
              class="flex items-center gap-1.5 cursor-pointer border-none bg-transparent p-0 outline-none"
            >
              <AvatarRoot class="w-8 h-8 rounded-full border-2 border-border-default hover:border-(--color-primary) transition-colors duration-150">
                <AvatarImage
                  v-if="userStore.user.photo"
                  :src="userStore.user.photo"
                  alt="用户头像"
                  class="w-full h-full rounded-full object-cover"
                />
                <AvatarFallback
                  :delay-ms="600"
                  class="w-full h-full flex items-center justify-center bg-(--color-primary) text-white text-sm font-semibold rounded-full"
                >
                  {{ userInitial }}
                </AvatarFallback>
              </AvatarRoot>
              <ChevronDown class="size-3 text-text-tertiary transition-transform duration-200" />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent class="z-200">
            <!-- 用户信息 -->
            <div class="px-3 py-2.5">
              <p class="text-sm font-semibold text-text-primary leading-tight">{{ displayName }}</p>
              <p class="text-xs text-text-tertiary leading-tight mt-0.5">@{{ userStore.user.username }}</p>
            </div>

            <DropdownMenuSeparator />

            <DropdownMenuItem class="cursor-pointer" @click="goUserCenter">
              用户中心
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem class="text-(--color-destructive) cursor-pointer" @click="handleLogout">
              退出登录
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </nav>

      <!-- 移动端汉堡按钮 -->
      <button
        type="button"
        class="lg:hidden flex items-center justify-center w-10 h-10 rounded-[10px] border border-border-default bg-transparent cursor-pointer"
        aria-label="Toggle menu"
        @click="toggleMenu"
      >
        <X v-if="mobileMenuOpen" :size="20" class="text-text-secondary" />
        <Menu v-else :size="20" class="text-text-secondary" />
      </button>
    </div>

    <!-- 移动端菜单面板 -->
    <div
      class="lg:hidden absolute top-full left-0 right-0 bg-surface-card shadow-[var(--shadow-lg)] overflow-hidden transition-all duration-300 ease-out"
      :class="mobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'"
    >
      <div class="container-main py-4 flex flex-col">
        <!-- 导航链接 -->
        <nav aria-label="移动端导航" class="flex flex-col gap-1">
          <a
            v-for="link in sectionLinks"
            :key="link.hash"
            role="button"
            tabindex="0"
            class="w-full text-left px-4 py-3 rounded-[10px] text-sm font-medium transition-all duration-150 cursor-pointer border-none no-underline"
            :class="isSectionActive(link.hash)
              ? 'bg-(--color-primary-light) text-(--color-primary) font-semibold'
              : 'bg-transparent text-text-secondary hover:bg-(--color-primary-subtle)'"
            @click="scrollToSection(link.hash)"
          >
            {{ link.label }}
          </a>
          <a
            v-if="isLoggedIn"
            role="button"
            tabindex="0"
            class="w-full text-left px-4 py-3 rounded-[10px] text-sm font-medium transition-all duration-150 cursor-pointer border-none no-underline bg-transparent text-text-secondary hover:bg-(--color-primary-subtle)"
            @click="goUserCenter"
          >
            用户中心
          </a>
        </nav>

        <!-- 分隔线 -->
        <div class="border-t border-border-subtle my-3"></div>

        <!-- 操作按钮 -->
        <div class="flex flex-col gap-2">
          <template v-if="!isLoggedIn">
            <button
              type="button"
              class="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-[10px] text-sm font-semibold transition-all duration-250 cursor-pointer border-none bg-(--color-primary) text-white hover:bg-(--color-primary-hover)"
              @click="handleLogin"
            >
              登录
            </button>
          </template>

          <template v-else>
            <!-- 已登录用户信息 -->
            <div class="flex items-center gap-3 px-4 py-3">
              <AvatarRoot class="w-10 h-10 rounded-full border-2 border-border-default shrink-0">
                <AvatarImage
                  v-if="userStore.user.photo"
                  :src="userStore.user.photo"
                  alt="用户头像"
                  class="w-full h-full rounded-full object-cover"
                />
                <AvatarFallback
                  :delay-ms="600"
                  class="w-full h-full flex items-center justify-center bg-(--color-primary) text-white text-sm font-semibold rounded-full"
                >
                  {{ userInitial }}
                </AvatarFallback>
              </AvatarRoot>
              <div class="flex flex-col min-w-0">
                <span class="text-sm font-semibold text-text-primary leading-tight">{{ displayName }}</span>
                <span class="text-xs text-text-tertiary leading-tight mt-0.5">@{{ userStore.user.username }}</span>
              </div>
            </div>

            <button
              type="button"
              class="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-[10px] text-sm font-semibold transition-all duration-250 cursor-pointer border-none bg-red-50 text-(--color-destructive) hover:bg-red-100"
              @click="handleLogout"
            >
              退出登录
            </button>
          </template>
        </div>
      </div>
    </div>
  </header>
</template>
